// historyAdapter.ts
export type UiHistory = {
  id: string
  entryLabel: string
  user: {
    userId: string
    mailAdress: string
  }
  batchId: string
  changeDate: Date | string
  reportType: string
  previusReportVersion: any
  changesThatWasMade: {
    added: any[]
    updated: any[]
    deleted: any[]
    csrdAdded: any[]
    csrdDeleted: any[]
    invoiceReportFieldsUpdated: any | null
    kickbackReportChange: any | null
  }
}

export function mapHistoryToUi(history: any): UiHistory {
  // Backend may return NormalizedHistoryEntry (new) or legacy EditedReportsHistory DTOs (old).
  // We normalize both into the legacy-friendly UiHistory shape used by DisplayHistory.
  const rawChanges =
    history?.changesThatWasMade ??
    history?.changes ??
    history?.normalizedChanges ??
    {}

  const rawPrev =
    history?.previusReportVersion ??
    history?.previousReportVersion ??
    history?.normalizedPreviousReport ??
    history?.previousReportVersion ??
    history?.PreviousReportVersion ??
    history?.previousReportVersion ??
    history?.PreviousReport ??
    history?.previousReport ??
    history?.PreviousReportVersion ??
    {}

  const rawUser = history?.user ?? history?.User ?? {}

  const normalizedId =
    history?.id ??
    history?.Id ??
    history?.historyId ??
    history?.HistoryId ??
    ''

  // If we got NormalizedHistoryEntry, it doesn't have ChangesThatWasMade.
  // We synthesize a legacy-like ChangeContainer so existing UI (DisplayHistory) can render.
  const isNormalizedEntry =
    typeof history?.historyId !== 'undefined' ||
    typeof history?.HistoryId !== 'undefined'

  const normalizedRowChanges: any[] =
    history?.rowChanges ?? history?.RowChanges ?? []

  const normalizedReportChanges: any[] =
    history?.reportChanges ?? history?.ReportChanges ?? []

  const normalizedChangesThatWasMade = (() => {
    if (!isNormalizedEntry) return null

    const isEntityType = (c: any, expected: string) => {
      const t = String(c?.entityType ?? c?.EntityType ?? '')
      if (t === expected) return true
      // Some normalizations emit entityType="JObject"; fall back to shape detection.
      const v = c?.currentVersion ?? c?.CurrentVersion ?? c?.previousVersion ?? c?.PreviousVersion
      const newValues = c?.newValues ?? c?.NewValues ?? {}
      const has = (k: string) => typeof (v?.[k] ?? v?.[k.charAt(0).toLowerCase() + k.slice(1)] ?? newValues?.[k] ?? newValues?.[k.charAt(0).toLowerCase() + k.slice(1)]) !== 'undefined'
      if (expected === 'Kickback') {
        return has('ContractId') && (has('SumExclMoms') || has('Comment') || has('ReportId'))
      }
      if (expected === 'KickbackProject') {
        return has('ContractId') && (has('ProjectName') || has('InvoiceReference') || has('InvoiceIdentifier'))
      }
      return false
    }

    const addedInvoices = normalizedRowChanges
      .filter((c) => c?.entityType === 'Invoice' && c?.changeKind === 'Added')
      .map((c) => c?.currentVersion)
      .filter(Boolean)

    const deletedInvoices = normalizedRowChanges
      .filter((c) => c?.entityType === 'Invoice' && c?.changeKind === 'Deleted')
      .map((c) => c?.previousVersion)
      .filter(Boolean)

    const csrdAdded = normalizedRowChanges
      .filter((c) => c?.entityType === 'InvoiceRow' && c?.changeKind === 'Added')
      .map((c) => c?.currentVersion)
      .filter(Boolean)

    const csrdDeleted = normalizedRowChanges
      .filter((c) => c?.entityType === 'InvoiceRow' && c?.changeKind === 'Deleted')
      .map((c) => c?.previousVersion)
      .filter(Boolean)

    const updated = normalizedRowChanges
      .filter((c) => c?.changeKind === 'Updated')
      .map((c) => {
        const newValues = c?.newValues ?? {}
        const properties = Object.entries(newValues).map(([field, value]) => ({
          field,
          value,
        }))
        return {
          rowId: c?.entityId ?? c?.EntityId ?? 0,
          currentRowVersion: c?.currentVersion ?? c?.CurrentVersion ?? c?.previousVersion,
          properties,
        }
      })

    const invoiceReportFieldsUpdated =
      normalizedReportChanges.length > 0
        ? {
            properties: normalizedReportChanges.map((c) => ({
              field: c?.field,
              value: c?.newValue,
            })),
            oldValues: normalizedReportChanges.map((c) => ({
              field: c?.field,
              value: c?.oldValue,
            })),
          }
        : null

    const kickback = normalizedRowChanges
      .filter((c) => isEntityType(c,'Kickback') && c?.changeKind === 'Updated')
      .map((c) => c?.currentVersion ?? c?.CurrentVersion)
      .filter(Boolean)

    const kickbackProject = normalizedRowChanges
      .filter((c) => isEntityType(c,'KickbackProject') && c?.changeKind === 'Updated')
      .map((c) => c?.currentVersion ?? c?.CurrentVersion)
      .filter(Boolean)

    const kickbackReportChange =
      kickback.length > 0 || kickbackProject.length > 0
        ? { kickback, kickbackProject }
        : null

    return {
      added: addedInvoices,
      updated,
      deleted: deletedInvoices,
      csrdAdded,
      csrdDeleted,
      invoiceReportFieldsUpdated,
      kickbackReportChange,
    }
  })()

  return {
    id: String(normalizedId),
    entryLabel:
      history?.entryLabel ??
      history?.EntryLabel ??
      history?.movement?.actionType ??
      history?.Movement?.ActionType ??
      'Edit',
    user: {
      userId: String(rawUser?.userId ?? rawUser?.UserId ?? ''),
      mailAdress:
        rawUser?.mailAdress ??
        rawUser?.MailAdress ??
        rawUser?.email ??
        ''
    },
    batchId: history?.batchId ?? history?.BatchId ?? history?.BatchId ?? '',
    changeDate: history?.changeDate ?? history?.ChangeDate ?? history?.changeDate ?? history?.ChangeDate ?? '',
    reportType: history?.reportType ?? history?.ReportType ?? history?.reportType ?? history?.ReportType ?? '',
    previusReportVersion: {
      Invoices: rawPrev?.Invoices ?? rawPrev?.invoices ?? [],
      Report: rawPrev?.Report ?? rawPrev?.report ?? null,
      CsrdRows: rawPrev?.CsrdRows ?? rawPrev?.csrdRows ?? [],
      Kickback: rawPrev?.Kickback ?? rawPrev?.kickback ?? [],
      KickbackProject: rawPrev?.KickbackProject ?? rawPrev?.kickbackProject ?? []
    },
    changesThatWasMade: {
      added: (normalizedChangesThatWasMade?.added ?? rawChanges?.added ?? rawChanges?.Added ?? []),
      updated: (normalizedChangesThatWasMade?.updated ?? rawChanges?.updated ?? rawChanges?.Updated ?? []),
      deleted: (normalizedChangesThatWasMade?.deleted ?? rawChanges?.deleted ?? rawChanges?.Deleted ?? []),
      csrdAdded: (normalizedChangesThatWasMade?.csrdAdded ?? rawChanges?.csrdAdded ?? rawChanges?.CsrdAdded ?? []),
      csrdDeleted: (normalizedChangesThatWasMade?.csrdDeleted ?? rawChanges?.csrdDeleted ?? rawChanges?.CsrdDeleted ?? []),
      invoiceReportFieldsUpdated:
        normalizedChangesThatWasMade?.invoiceReportFieldsUpdated ??
        rawChanges?.invoiceReportFieldsUpdated ??
        rawChanges?.InvoiceReportFieldsUpdated ??
        null,
      kickbackReportChange:
        normalizedChangesThatWasMade?.kickbackReportChange ??
        rawChanges?.kickbackReportChange ??
        rawChanges?.KickbackReportChange ??
        null
    }
  }
}

export function mapHistoryListToUi(data: any[]): UiHistory[] {
  if (!Array.isArray(data)) return []
  return data.map(mapHistoryToUi)
}
